
import { LeaderboardHeader } from "@/components/leaderboard/LeaderboardHeader";
import { HomeLink } from "@/components/HomeLink";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLeaderboard } from "@/hooks/use-leaderboard";
import { LeaderboardEntry } from "@/components/leaderboard/LeaderboardEntry";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Award, Trophy, Swords } from "lucide-react";

const Leaderboards = () => {
  const { leaderboardData: globalLeaderboard, isLoading: isGlobalLoading } = useLeaderboard();

  const { data: ambassadorLeaderboard, isLoading: isAmbassadorLoading } = useQuery({
    queryKey: ["ambassador-leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_ambassador', true)
        .order('points', { ascending: false })
        .limit(10);
      
      if (error) throw error;

      // Calculate referral counts
      const referralCodes = (data || []).map(p => p.referral_code).filter(Boolean);
      let referralCounts = new Map<string, number>();
      if (referralCodes.length > 0) {
        const { data: referred } = await supabase
          .from('profiles')
          .select('referred_by')
          .in('referred_by', referralCodes as string[]);
        (referred || []).forEach(r => {
          if (r.referred_by) referralCounts.set(r.referred_by, (referralCounts.get(r.referred_by) || 0) + 1);
        });
      }

      return (data || []).map((entry, index) => ({
        rank: index + 1,
        name: entry.display_name || entry.username || 'Anonymous',
        score: referralCounts.get(entry.referral_code || '') || 0,
        country: entry.country || 'Unknown'
      }));
    }
  });

  const { data: multiplayerLeaderboard, isLoading: isMultiplayerLoading } = useQuery({
    queryKey: ["multiplayer-leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('multiplayer_leaderboard')
        .select('*')
        .order('total_points', { ascending: false })
        .order('wins', { ascending: false })
        .limit(10);
      
      if (error) throw error;

      // Fetch profiles
      const userIds = (data || []).map(e => e.user_id);
      const { data: profiles } = userIds.length > 0
        ? await supabase.from('profiles').select('id, display_name, username, country').in('id', userIds)
        : { data: [] };
      
      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      return (data || []).map(entry => ({
        ...entry,
        profile: profileMap.get(entry.user_id) || null,
      }));
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gamePurple to-gameSlate p-4 md:p-8">
      <HomeLink />
      <div className="max-w-4xl mx-auto mt-8">
        <h1 className="text-4xl font-bold text-gameGold text-center mb-8">Leaderboards</h1>
        
        <Tabs defaultValue="global" className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="global" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Global</span>
            </TabsTrigger>
            <TabsTrigger value="ambassador" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Ambassadors</span>
            </TabsTrigger>
            <TabsTrigger value="multiplayer" className="flex items-center gap-2">
              <Swords className="w-4 h-4" />
              <span className="hidden sm:inline">Multiplayer</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="global" className="mt-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gameGold mb-4">Global Rankings</h2>
              {isGlobalLoading ? (
                <p className="text-center text-gray-400">Loading global rankings...</p>
              ) : (
                globalLeaderboard?.map((entry) => (
                  <LeaderboardEntry key={entry.rank} {...entry} />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="ambassador" className="mt-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gameGold mb-4">Top Ambassadors</h2>
              {isAmbassadorLoading ? (
                <p className="text-center text-gray-400">Loading ambassador rankings...</p>
              ) : (
                ambassadorLeaderboard?.map((entry) => (
                  <LeaderboardEntry key={entry.rank} {...entry} />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="multiplayer" className="mt-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gameGold mb-4">Multiplayer Champions</h2>
              {isMultiplayerLoading ? (
                <p className="text-center text-gray-400">Loading multiplayer rankings...</p>
              ) : (
                <div className="bg-gamePurple/30 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-4 gap-4 p-4 border-b border-gameGold/20 font-bold text-gameGold">
                    <div>Player</div>
                    <div>Wins</div>
                    <div>Losses</div>
                    <div>Points</div>
                  </div>
                  {multiplayerLeaderboard?.map((entry) => (
                    <div key={entry.id} className="grid grid-cols-4 gap-4 p-4 border-b border-gameGold/10">
                      <div className="text-gameGold">
                        {entry.profile?.display_name || entry.profile?.username || 'Unknown Player'}
                      </div>
                      <div className="text-green-500">{entry.wins}</div>
                      <div className="text-red-500">{entry.losses}</div>
                      <div className="text-gameGold">{entry.total_points}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Leaderboards;
